import {useEffect, useState} from 'react';

import {VehicleAlbum} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {TestIdProps, suffixTestId} from 'shared/web';

import {UploadedFile} from '../../../services/UploadedFile';
import {noop} from '../../../utils/someTeasUtils';
import {DefaultMultiSelectComponent} from '../../CustomDragAndDrop';
import {DragAndDropProvider} from '../../CustomDragAndDrop/DragAndDropProvider';
import {DraggableDataWithContextId} from '../../CustomDragAndDrop/types';
import {ImageType} from '../../ImagesCard/types';
import {ImagesCardConnected} from '../../ImagesCardConnected/ImagesCardConnected';

type Props = {
  images: ImageType[];
  album?: VehicleAlbum;
  onImageClick?: (id: string) => void;
  onChange: (files: UploadedFile[], album?: VehicleAlbum) => Promise<void>;
  onDelete: (photos: string[]) => void;
  onDragEnd: (
    items: DraggableDataWithContextId<ImageType>[],
    position: number,
    droppedOn?: DraggableDataWithContextId<ImageType>
  ) => void;
  isAddForm?: boolean;
  isUploadDisabled?: boolean;
  isDeleteDisabled?: boolean;
} & TestIdProps;

export const UploadPhotos = ({
  album,
  onChange,
  onDelete,
  images,
  isAddForm,
  onDragEnd,
  onImageClick,
  ...props
}: Props) => {
  const [isSelectionEnabled, setIsSelectionEnabled] = useState(false);
  const [imagesState, setImagesState] = useState<ImageType[]>([]);

  useEffect(
    function syncImagesState() {
      setImagesState(images);
    },
    [images]
  );

  const handleNewImagesSelection = (id: string) => {
    setImagesState(
      imagesState.map((image) =>
        image.id === id ? {...image, isSelected: !image.isSelected} : image
      )
    );
  };

  const handleSelectionToggle = (reset = false) => {
    if (reset) {
      setImagesState(images.map((image) => ({...image, isSelected: false})));
      setIsSelectionEnabled(false);
    } else {
      setIsSelectionEnabled((prev) => !prev);
    }
  };

  return (
    <DragAndDropProvider
      onDragStart={() => {
        if (isSelectionEnabled) {
          handleSelectionToggle();
        }
      }}
      onDragEnd={onDragEnd}
      onDragFail={() => {
        if (imagesState.some((i) => i.isSelected)) {
          handleSelectionToggle();
        }
      }}
      selectedItems={imagesState.filter((i) => i.isSelected).map((i) => i.id)}
      multipleSelectionComponent={DefaultMultiSelectComponent}
      data-testid={suffixTestId('dragAndDropProvider', props)}
    >
      <ImagesCardConnected
        data-testid={props['data-testid']}
        albums={[]}
        album={album}
        globalListener
        droppableId={album?.id || undefined}
        cardProps={{title: i18n.t('entity.photo.labels.images')}}
        images={isAddForm ? imagesState : images}
        onDelete={(photos) => onDelete(photos.map((p) => p.id))}
        onUpload={onChange}
        onMove={noop}
        onCopy={noop}
        onSetAsCover={noop}
        selectionEnabled={isSelectionEnabled}
        isUploadDisabled={props.isUploadDisabled}
        isDeleteDisabled={props.isDeleteDisabled}
        disableDrag={props.isUploadDisabled}
        onImageClick={onImageClick}
        onSelectionToggle={() => handleSelectionToggle(true)}
        onImageSelect={(id: string) => {
          if (isAddForm) {
            handleNewImagesSelection(id);
          }
        }}
        isAddForm={isAddForm}
      />
    </DragAndDropProvider>
  );
};
